import type { TFunction } from "i18next";
import { FormSection } from "../../components/FormSection";
import { SelectInput } from "../../components/SelectInput";
import { ColorInput } from "../../components/ColorInput";
import { useCatalogScope } from "../../hooks/useCatalogScope";
import { useGetBookkeepingAccountsQuery } from "../../query/bookkeepingAccountsQuery";
import { PaymentMethodType } from "@KassaroAB/shared/types/enums/PaymentMethodType";
import type { BookkeepingAccountId } from "@KassaroAB/shared/types/ids";
import { FieldRow, Fields, TextField } from "./forms/paymentMethodFields";
import { withFieldGroup } from "./paymentMethodFormHook";

type PaymentMethodFormData = {
  name: string;
  type: PaymentMethodType;
  bookkeepingAccountId: BookkeepingAccountId | null;
  color: string | null;
};

const defaultValues: PaymentMethodFormData = {
  name: "",
  type: PaymentMethodType.CASH,
  bookkeepingAccountId: null,
  color: null,
};

type BookkeepingAccountSelectProps = {
  value: BookkeepingAccountId | null;
  onChange: (value: BookkeepingAccountId | null) => void;
  onBlur: () => void;
  defaultLabel: string;
};

const BookkeepingAccountSelect = ({
  value,
  onChange,
  onBlur,
  defaultLabel,
}: BookkeepingAccountSelectProps) => {
  const { scope } = useCatalogScope();
  const { data: accounts } = useGetBookkeepingAccountsQuery(scope);
  return (
    <SelectInput
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      onBlur={onBlur}
    >
      <option value="">{defaultLabel}</option>
      {accounts?.map((account) => (
        <option key={account.id} value={account.id}>
          {account.accountNumber} {account.name}
        </option>
      ))}
    </SelectInput>
  );
};

export const FieldGroupPaymentMethodFields = withFieldGroup({
  defaultValues,
  props: {
    t: undefined as unknown as TFunction,
    defaultAccountNumber: "1930",
  },
  render: ({ group, t, defaultAccountNumber }) => (
    <FormSection title={t("paymentMethodForm.basicInformation")}>
      <Fields>
        <group.AppField name="name">
          {(field) => (
            <TextField
              label={t("paymentMethodForm.name")}
              value={field.state.value}
              onChange={field.handleChange}
              onBlur={field.handleBlur}
            />
          )}
        </group.AppField>
        <group.AppField name="type">
          {(field) => (
            <TextField
              label={t("paymentMethodForm.type")}
              value={t(`paymentMethodType.${field.state.value}`, {
                defaultValue: field.state.value,
              })}
              onChange={() => {}}
              disabled
            />
          )}
        </group.AppField>
        <group.AppField name="bookkeepingAccountId">
          {(field) => (
            <FieldRow label={t("paymentMethodForm.bookkeepingAccount")}>
              <BookkeepingAccountSelect
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                defaultLabel={`${t("paymentMethodForm.defaultAccount")}: ${defaultAccountNumber}`}
              />
            </FieldRow>
          )}
        </group.AppField>
        <group.AppField name="color">
          {(field) => (
            <FieldRow label={t("paymentMethodForm.color")}>
              <ColorInput
                value={field.state.value}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                clearLabel={t("paymentMethodForm.clearColor")}
              />
            </FieldRow>
          )}
        </group.AppField>
      </Fields>
    </FormSection>
  ),
});
